/**
 * GTM (Google Tag Manager) 动态加载器
 * @description 根据品牌配置和环境动态加载 GTM 容器
 */

export interface GTMLoaderOptions {
  /** GTM 容器 ID (如 GTM-XXXXXXX) */
  gtmId: string
  /** 是否启用 (仅生产环境启用) */
  enabled?: boolean
  /** dataLayer 名称 (默认 'dataLayer') */
  dataLayerName?: string
  /** 是否开启调试模式 */
  debug?: boolean
}

/**
 * 检查是否为生产环境
 */
function isProductionEnv(): boolean {
  if (typeof import.meta !== 'undefined') {
    const env = (import.meta as { env?: { PROD?: boolean; MODE?: string } }).env
    return Boolean(env?.PROD) || env?.MODE === 'production'
  }
  return false
}

/**
 * 加载 GTM 脚本
 * @description 动态注入 GTM 脚本到页面头部
 */
export function loadGTM(options: GTMLoaderOptions): void {
  const { gtmId, enabled = isProductionEnv(), dataLayerName = 'dataLayer', debug = false } = options

  // 检查是否应该加载
  if (!enabled) {
    if (debug) {
      console.log('[GTM] Skipped loading - not in production environment')
    }
    return
  }

  if (!gtmId) {
    if (debug) {
      console.warn('[GTM] No GTM ID provided, skipping load')
    }
    return
  }

  // 检查是否已加载
  if (typeof window === 'undefined') {
    return
  }

  interface GTMWindow {
    gtmLoaded?: boolean
    dataLayer?: unknown[]
    [key: string]: unknown
  }

  const win = window as unknown as GTMWindow

  if (win.gtmLoaded) {
    if (debug) {
      console.log('[GTM] Already loaded, skipping')
    }
    return
  }

  // 初始化 dataLayer
  const existingLayer = win[dataLayerName]
  const dataLayer: unknown[] = Array.isArray(existingLayer) ? existingLayer : []
  win[dataLayerName] = dataLayer

  dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  })

  // 创建并注入脚本
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${
    dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
  }`

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  win.gtmLoaded = true

  if (debug) {
    console.log(`[GTM] Loaded container: ${gtmId}`)
  }
}

/**
 * 创建品牌 GTM 加载器
 * @description 根据品牌配置创建 GTM 加载函数
 *
 * @example
 * ```ts
 * // 在品牌配置中
 * export const vigilkidsConfig = {
 *   analytics: {
 *     enabled: true,
 *     gtmId: 'GTM-VIGILKIDS',
 *     modulePrefix: 'vk_',
 *   }
 * };
 *
 * // 在应用入口
 * import { createBrandGTMLoader } from '@checkout/core';
 * const loadBrandGTM = createBrandGTMLoader(brandConfig.analytics);
 * loadBrandGTM(); // 自动判断环境
 * ```
 */
export function createBrandGTMLoader(
  analyticsConfig: {
    enabled: boolean
    gtmId?: string
  },
  options?: Omit<GTMLoaderOptions, 'gtmId' | 'enabled'>
) {
  return () => {
    if (!analyticsConfig.enabled || !analyticsConfig.gtmId) {
      return
    }

    loadGTM({
      gtmId: analyticsConfig.gtmId,
      enabled: analyticsConfig.enabled && isProductionEnv(),
      ...options,
    })
  }
}

/**
 * 检查 GTM 是否已加载
 */
export function isGTMLoaded(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return Boolean((window as unknown as { gtmLoaded?: boolean }).gtmLoaded)
}

/**
 * 推送事件到 dataLayer
 * @description 直接推送事件到 GTM dataLayer
 */
export function pushToDataLayer(event: Record<string, unknown>, dataLayerName = 'dataLayer'): void {
  if (typeof window === 'undefined') {
    return
  }

  const win = window as unknown as { [key: string]: unknown[] }
  win[dataLayerName] = win[dataLayerName] || []
  win[dataLayerName].push(event)
}
